import { useEffect, useState } from "react";
import { useSeo } from "@/hooks/useSeo";
import FaqSection from "@/components/faq-section";
import { PAGE_FAQS } from "@/data/faqs";
import { useBreadcrumbs } from "@/hooks/useBreadcrumbs";
import { openBookingModal } from "@/components/booking-modal";

export default function Svadba() {
  const [loaded, setLoaded] = useState(false);

  useSeo({
    title: "Свадьба на крыше в Петербурге — Ресторан Паруса на крыше",
    description: "Свадьба на крыше Петербурга с панорамным видом на город. Выездная регистрация, банкет до 300 гостей, живая музыка. Организация под ключ. Ул. Марата, 5.",
    keywords: "свадьба на крыше Петербург, свадебный банкет СПб, выездная регистрация ресторан, свадьба с видом на город Петербург, свадьба ресторан ул Марата, свадебный зал крыша СПб, свадьба Olympic Plaza",
    image: "/photos/view_summer.jpg",
    canonical: "/svadba",
  });
  useBreadcrumbs([{ name: "Свадьба", path: "/svadba" }]);

  useEffect(() => { setTimeout(() => setLoaded(true), 100); }, []);

  return (
    <div className="w-full bg-background">
      {/* Hero */}
      <section className="relative h-[80vh] w-full flex items-center justify-center overflow-hidden border-b border-primary/20">
        <div className="absolute inset-0 z-0">
          <img src="/photos/view_summer.webp" alt="Свадьба на крыше Петербурга" className="w-full h-full object-cover" style={{ filter: "brightness(0.22) saturate(0.5)", transform: "scale(1.05)" }} loading="eager" fetchPriority="high" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(5,20,10,0.95) 0%, rgba(10,30,18,0.88) 100%)" }} />
        </div>
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto" style={{ opacity: loaded ? 1 : 0, transform: loaded ? "translateY(0)" : "translateY(30px)", transition: "all 1s ease 0.3s" }}>
          <div className="inline-block px-6 py-2 border border-primary/30 mb-8">
            <span className="text-[9px] tracking-[0.3em] uppercase text-accent">Свадьба</span>
          </div>
          <h1 className="text-5xl md:text-7xl font-serif font-light text-foreground mb-6 leading-[1.1]">
            Свадьба на крыше<br /><span className="text-accent italic">в Петербурге</span>
          </h1>
          <p className="text-white/60 text-lg font-light mb-10 max-w-xl mx-auto leading-relaxed">
            Выездная регистрация, банкет и незабываемые фото — с панорамой всего Петербурга
          </p>
          <button onClick={openBookingModal} className="bg-primary/20 border border-primary text-accent px-12 py-4 text-[10px] tracking-[0.3em] uppercase font-medium hover:bg-primary hover:text-background transition-colors">
            Обсудить свадьбу
          </button>
        </div>
      </section>

      {/* Why */}
      <section className="py-32 bg-card border-b border-primary/10">
        <div className="container mx-auto px-6 md:px-14 max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="text-[9px] tracking-[0.4em] text-primary/50 uppercase mb-5">Место для свадьбы</div>
              <h2 className="text-4xl md:text-5xl font-serif font-light text-foreground mb-8 leading-tight">
                Петербург —<br /><span className="text-accent italic">ваш свадебный фон</span>
              </h2>
              <p className="text-muted-foreground font-light leading-relaxed mb-6">
                6-й этаж Olympic Plaza в самом сердце города. Открытая терраса с видом 300° на крыши Петербурга, застеклённый зал с живыми растениями, панорамные окна — каждый кадр здесь выглядит как с обложки.
              </p>
              <p className="text-muted-foreground font-light leading-relaxed mb-10">
                Мы организуем всё под ключ: выездную регистрацию, декор, живую музыку, авторское меню, торт и развлекательную программу. Вам остаётся только наслаждаться своим днём.
              </p>
              <button onClick={() => document.getElementById("formats")?.scrollIntoView({ behavior: "smooth" })} className="border border-primary/40 text-accent px-10 py-4 text-[10px] tracking-[0.3em] uppercase hover:border-primary transition-colors">
                Форматы свадьбы →
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <img src="/photos/view_sunset.webp" alt="Закат над Петербургом" className="w-full aspect-square object-cover border border-primary/10" loading="lazy" />
              <img src="/photos/view_clouds.webp" alt="Вид с крыши" className="w-full aspect-square object-cover border border-primary/10 mt-6" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      {/* Formats */}
      <section id="formats" className="py-32 bg-background border-b border-primary/10">
        <div className="container mx-auto px-6 md:px-14 max-w-6xl">
          <div className="mb-16">
            <div className="text-[9px] tracking-[0.4em] text-primary/50 uppercase mb-4">Форматы</div>
            <h2 className="text-4xl md:text-5xl font-serif font-light text-foreground">Как провести свадьбу</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { num: "01", title: "Камерная свадьба", desc: "VIP-зал до 20 гостей с персональным обслуживанием. Идеально для близкого круга и романтической атмосферы.", price: "от 60 000 ₽ депозит" },
              { num: "02", title: "Банкет на террасе", desc: "Полное закрытие террасы под свадьбу с открытым небом над Петербургом. Живая музыка, танцевальная зона.", price: "от 1 000 000 ₽" },
              { num: "03", title: "Свадьба под ключ", desc: "Полное закрытие ресторана до 300 гостей. Организация всего: декор, артисты, ведущий, меню, торт.", price: "от 2 000 000 ₽" },
            ].map((f) => (
              <div key={f.num} className="border border-primary/20 p-10 bg-card group hover:border-primary/40 transition-colors">
                <div className="font-serif text-5xl font-light text-primary/15 mb-6 group-hover:text-primary/35 transition-colors">{f.num}</div>
                <h3 className="font-serif text-2xl font-light text-foreground mb-3">{f.title}</h3>
                <p className="text-sm text-muted-foreground font-light leading-relaxed mb-6">{f.desc}</p>
                <div className="text-[10px] tracking-[0.2em] uppercase text-primary/60 border-t border-primary/15 pt-4">{f.price}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-32 bg-card border-b border-primary/10">
        <div className="container mx-auto px-6 md:px-14 max-w-6xl">
          <div className="mb-14">
            <h2 className="text-4xl md:text-5xl font-serif font-light text-foreground">Что входит в организацию</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              "Выездная регистрация", "Живая музыка", "Авторское меню", "Свадебный торт",
              "Декор и цветы", "Ведущий и DJ", "Фотозона", "Персональный менеджер",
            ].map((item) => (
              <div key={item} className="flex items-center gap-3 p-4 border border-primary/15">
                <span className="text-primary/50 text-xs shrink-0">✦</span>
                <span className="text-sm text-muted-foreground font-light">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FaqSection items={PAGE_FAQS["/svadba"]} title="Вопросы о свадьбе" />

      {/* CTA */}
      <section className="py-28 bg-background">
        <div className="container mx-auto px-6 text-center max-w-2xl">
          <h2 className="text-4xl font-serif font-light text-foreground mb-6">Начнём планировать?</h2>
          <p className="text-muted-foreground font-light mb-10 leading-relaxed">
            Оставьте заявку — свадебный менеджер свяжется с вами в течение часа и ответит на все вопросы.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={openBookingModal} className="bg-primary border border-primary text-background px-12 py-4 text-[10px] tracking-[0.3em] uppercase font-medium hover:bg-primary/80 transition-colors">
              Обсудить детали
            </button>
            <a href="/banket#banquet-form" className="border border-primary/40 text-accent px-12 py-4 text-[10px] tracking-[0.3em] uppercase font-medium hover:border-primary transition-colors">
              Оставить заявку
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
